import React, { useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, XCircle, X, Clock, MessageCircle, Shield, BookOpen, Heart, AlertTriangle, Check, ChevronDown, Lock } from 'lucide-react';
type LegalModal = 'terms' | 'privacy' | 'refund' | null;
const legalContent: Record<Exclude<LegalModal, null>, {
  title: string;
  body: React.ReactNode;
}> = {
  terms: {
    title: 'Terms & Conditions',
    body: <div className="space-y-4 text-sm text-charcoal/80 leading-relaxed" data-id="element-0">
        <p data-id="element-1">
          <strong data-id="element-2">1. Acceptance of Terms</strong>
          <br data-id="element-3" />
          By purchasing and downloading the manual "Two Houses, the Kids &
          Divorce: The Mirror House Method," you accept these terms and
          conditions in full.
        </p>
        <p data-id="element-4">
          <strong data-id="element-5">2. Digital Product</strong>
          <br data-id="element-6" />
          This product is a downloadable PDF file. Once the purchase is
          complete, you will receive immediate access to the file. The content
          is for personal use only and may not be redistributed, copied, or
          resold.
        </p>
        <p data-id="element-7">
          <strong data-id="element-8">3. Use of Content</strong>
          <br data-id="element-9" />
          The material in this manual is for educational and informational
          purposes only. It does not constitute legal, medical, or professional
          psychological advice. Please consult a qualified professional for
          specific situations.
        </p>
        <p data-id="element-10">
          <strong data-id="element-11">4. Intellectual Property</strong>
          <br data-id="element-12" />
          All content in the manual, including text, graphics, and design, is
          protected by copyright. Total or partial reproduction without express
          authorization is prohibited.
        </p>
        <p data-id="element-13">
          <strong data-id="element-14">5. Limitation of Liability</strong>
          <br data-id="element-15" />
          The author and distributor are not responsible for the results
          obtained from applying the techniques described. Every family
          situation is unique and results may vary.
        </p>
        <p data-id="element-16">
          <strong data-id="element-17">6. Modifications</strong>
          <br data-id="element-18" />
          We reserve the right to modify these terms at any time. Modifications
          will be effective upon publication.
        </p>
      </div>
  },
  privacy: {
    title: 'Privacy Policy',
    body: <div className="space-y-4 text-sm text-charcoal/80 leading-relaxed" data-id="element-19">
        <p data-id="element-20">
          <strong data-id="element-21">1. Information We Collect</strong>
          <br data-id="element-22" />
          We only collect the information necessary to process your purchase:
          name, email address, and payment details. We do not store credit card
          information.
        </p>
        <p data-id="element-23">
          <strong data-id="element-24">2. Use of Information</strong>
          <br data-id="element-25" />
          Your information is used exclusively to: process your order, deliver
          the digital product, and send purchase-related communications.
        </p>
        <p data-id="element-26">
          <strong data-id="element-27">3. Data Protection</strong>
          <br data-id="element-28" />
          We implement industry-standard security measures to protect your
          personal information against unauthorized access.
        </p>
        <p data-id="element-29">
          <strong data-id="element-30">4. We Do Not Share Your Information</strong>
          <br data-id="element-31" />
          We do not sell, trade, or transfer your personal information to third
          parties, except for the payment providers necessary to process your
          transaction.
        </p>
        <p data-id="element-32">
          <strong data-id="element-33">5. Contact</strong>
          <br data-id="element-34" />
          For any privacy inquiries, you can reach us through our support email.
        </p>
      </div>
  },
  refund: {
    title: 'Refund Policy',
    body: <div className="space-y-4 text-sm text-charcoal/80 leading-relaxed" data-id="element-35">
        <p data-id="element-36">
          <strong data-id="element-37">Digital Products</strong>
          <br data-id="element-38" />
          Due to the digital and immediate nature of this product, all sales are
          final once the file has been downloaded.
        </p>
        <p data-id="element-39">
          <strong data-id="element-40">Exceptions</strong>
          <br data-id="element-41" />
          If you experience technical issues that prevent you from accessing the
          file, contact us within 48 hours of purchase and we will resolve the
          issue or process a full refund.
        </p>
        <p data-id="element-42">
          <strong data-id="element-43">How to Request Support</strong>
          <br data-id="element-44" />
          Send an email with your proof of purchase and a description of the
          problem. We will respond within a maximum of 24 business hours.
        </p>
      </div>
  }
};
function LegalModalComponent({
  activeModal,
  onClose
}: {
  activeModal: Exclude<LegalModal, null>;
  onClose: () => void;
}) {
  const content = legalContent[activeModal];
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2
  }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose} data-id="element-45">
      <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 10
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 10
    }} transition={{
      duration: 0.25,
      ease: 'easeOut'
    }} className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-46">
        <div className="flex items-center justify-between p-6 border-b border-cream-dark" data-id="element-47">
          <h3 className="font-serif text-xl font-bold text-slate-800" data-id="element-48">
            {content.title}
          </h3>
          <button onClick={onClose} className="text-charcoal/50 hover:text-charcoal transition-colors p-1 rounded-lg hover:bg-cream" data-id="element-49">
            <X size={20} data-id="element-50" />
          </button>
        </div>
        <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)]" data-id="element-51">
          {content.body}
        </div>
      </motion.div>
    </motion.div>;
}
const FadeIn = ({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => <motion.div initial={{
  opacity: 0,
  y: 20
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-50px'
}} transition={{
  duration: 0.6,
  delay,
  ease: 'easeOut'
}} className={className} data-id="element-52">
    {children}
  </motion.div>;
const CHECKOUT_URL = 'https://guiadownload.com/products/two-homes-one-family-the-mirror-house-method';
const CTAButton = ({
  className = '',
  scrollTo
}: {
  className?: string;
  scrollTo?: string;
}) => <motion.a href={scrollTo ? undefined : CHECKOUT_URL} target={scrollTo ? undefined : '_blank'} rel={scrollTo ? undefined : 'noopener noreferrer'} whileHover={{
  scale: 1.02
}} whileTap={{
  scale: 0.98
}} onClick={e => {
  if (scrollTo) {
    e.preventDefault();
    document.getElementById(scrollTo)?.scrollIntoView({
      behavior: 'smooth'
    });
  }
}} className={`cursor-pointer w-full sm:w-auto bg-amber-600 hover:bg-amber-700 text-white font-bold py-4 px-8 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 flex flex-col items-center justify-center gap-1 no-underline ${className}`} data-id="element-53">
    <span className="text-lg sm:text-xl uppercase tracking-wide" data-id="element-54">
      Download My Guide Now
    </span>
    <span className="text-xs sm:text-sm font-normal opacity-90 flex items-center gap-1" data-id="element-55">
      <Lock size={14} data-id="element-56" /> Secure payment. Instant PDF access.
    </span>
  </motion.a>;
export function App() {
  const [activeModal, setActiveModal] = useState<LegalModal>(null);
  return <div className="min-h-screen bg-cream text-charcoal font-sans selection:bg-amber-200 selection:text-amber-900" data-id="element-57">
      {/* HERO SECTION */}
      <section className="bg-slate-800 text-cream pt-16 pb-20 px-4 relative overflow-hidden" data-id="element-58">
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-amber-500 via-transparent to-transparent" data-id="element-59"></div>
        <div className="max-w-4xl mx-auto relative z-10" data-id="element-60">
          <FadeIn className="text-center mb-10" data-id="element-61">
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl font-bold leading-tight mb-6 text-white" data-id="element-62">
              Two Houses, the Kids & Divorce: <br data-id="element-63" />
              <span className="text-amber-500 italic" data-id="element-64">
                The "Mirror House" Method
              </span>
            </h1>
            <p className="text-xl sm:text-2xl font-medium text-slate-300 max-w-3xl mx-auto leading-relaxed" data-id="element-65">
              Stop the tears at the handoff and protect your children's mental
              health without having to fight with your ex.
            </p>
          </FadeIn>

          <FadeIn delay={0.2} className="flex flex-col items-center" data-id="element-66">
            <div className="relative w-full max-w-2xl mb-10 rounded-xl overflow-hidden shadow-2xl shadow-black/50 border border-slate-700" data-id="element-67">
              <img src="https://cdn.magicpatterns.com/uploads/joq815MoKLTcsJFKUor4u6/heylloyd-image-1775242924226.png" alt="Two Houses, the Kids & Divorce - Book mockup on multiple devices" className="w-full h-auto object-cover" data-id="element-68" />
            </div>
            <CTAButton scrollTo="pricing" data-id="element-69" />
          </FadeIn>
        </div>
      </section>

      {/* THE HOOK */}
      <section className="py-20 px-4 bg-cream" data-id="element-70">
        <div className="max-w-3xl mx-auto" data-id="element-71">
          <FadeIn data-id="element-72">
            <div className="flex items-center gap-4 mb-6 text-amber-700" data-id="element-73">
              <Clock size={32} data-id="element-74" />
              <h2 className="font-serif text-3xl font-bold" data-id="element-75">
                It's 5:55 PM on a Sunday...
              </h2>
            </div>
            <div className="space-y-6 text-lg leading-relaxed text-charcoal/80" data-id="element-76">
              <p data-id="element-77">
                You're parked outside your ex's house and you feel that knot in
                your stomach you already know too well. You glance in the
                rearview mirror and see your child clutching their backpack, in
                silence or on the verge of tears.
              </p>
              <p data-id="element-78">
                The handoff moment arrives and chaos erupts:{' '}
                <strong data-id="element-79">
                  "I don't want to go!", "I want to stay with you!"
                </strong>
                . You feel a guilt that tears you apart and you wonder if you've
                ruined their life forever.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* THE PROBLEM */}
      <section className="py-20 px-4 bg-white border-y border-cream-dark" data-id="element-80">
        <div className="max-w-3xl mx-auto" data-id="element-81">
          <FadeIn data-id="element-82">
            <h2 className="font-serif text-3xl sm:text-4xl font-bold mb-8 text-slate-800" data-id="element-83">
              The trauma for a child isn't the divorce itself...
            </h2>
            <p className="text-xl font-medium text-amber-700 mb-8" data-id="element-84">
              It's the chaos of living between two worlds that don't
              communicate.
            </p>
            <div className="space-y-6 text-lg leading-relaxed text-charcoal/80" data-id="element-85">
              <p data-id="element-86">
                When the rules at Dad's house are "recess" and at Mom's house
                are "discipline," the child's brain enters a constant state of
                alert.
              </p>
              <p data-id="element-87">
                They don't know who they're supposed to be today. They dread
                transitions because leaving one house feels like losing a parent
                every week. That Sunday anxiety isn't rebellion — it's a cry for
                help caused by the lack of consistency.
              </p>
            </div>
          </FadeIn>

          <FadeIn delay={0.2} className="mt-16 bg-cream-dark/50 p-8 rounded-2xl" data-id="element-88">
            <h3 className="font-serif text-2xl font-bold mb-6 flex items-center gap-3 text-slate-800" data-id="element-89">
              <AlertTriangle className="text-amber-600" data-id="element-90" />
              Why what you've tried didn't work:
            </h3>
            <ul className="space-y-4" data-id="element-91">
              {['Trying to impose your rules on the other house through arguments only builds more defensive walls with your ex.', 'Waiting for the child to "just get used to it" over time only prolongs the post-separation stress.', 'Dropping all boundaries out of guilt ("poor thing, their parents split up") ends up creating a behavior problem that\'s much harder to fix.', 'Using the child to send messages or asking what happened at the other house puts them in the role of "referee" — an emotional burden no child can carry.'].map((item, i) => <li key={i} className="flex items-start gap-3 text-charcoal/80" data-id="element-92">
                  <XCircle className="text-red-500 shrink-0 mt-1" size={20} data-id="element-93" />
                  <span data-id="element-94">{item}</span>
                </li>)}
            </ul>
          </FadeIn>
        </div>
      </section>

      {/* THE MECHANISM */}
      <section className="py-24 px-4 bg-slate-800 text-cream" data-id="element-95">
        <div className="max-w-3xl mx-auto text-center" data-id="element-96">
          <FadeIn data-id="element-97">
            <Shield className="w-16 h-16 text-amber-500 mx-auto mb-6" data-id="element-98" />
            <h2 className="font-serif text-4xl sm:text-5xl font-bold mb-8 text-white" data-id="element-99">
              The "Mirror House" Method
            </h2>
            <div className="space-y-6 text-lg sm:text-xl leading-relaxed text-slate-300" data-id="element-100">
              <p data-id="element-101">
                This manual isn't about you and your ex becoming best friends.
                It's about creating an{' '}
                <strong data-id="element-102">"illusion of continuity."</strong>
              </p>
              <p data-id="element-103">
                By unifying invisible rules (sleep, screens, and language), we
                eliminate transition anxiety. The child stops feeling like
                they're changing planets and starts feeling like they're simply
                moving to their other bedroom.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* WHAT YOU GET */}
      <section className="py-20 px-4 bg-cream" data-id="element-104">
        <div className="max-w-4xl mx-auto" data-id="element-105">
          <FadeIn data-id="element-106">
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-center mb-16 text-slate-800" data-id="element-107">
              What's inside the guide?
            </h2>
            <div className="grid sm:grid-cols-2 gap-8" data-id="element-108">
              {[{
              icon: <MessageCircle data-id="element-109" />,
              title: 'The Adult Responsibility Script',
              desc: 'Exactly what to say when they ask "Why did you leave?"'
            }, {
              icon: <Clock data-id="element-110" />,
              title: 'The 15-Minute Rule',
              desc: 'The technique that eliminates 90% of fights and tears at handoff time.'
            }, {
              icon: <Shield data-id="element-111" />,
              title: 'The BIFF Method for Messages',
              desc: 'How to communicate with your ex professionally and drama-free.'
            }, {
              icon: <CheckCircle2 data-id="element-112" />,
              title: 'The Stress-Free Backpack Checklist',
              desc: 'So nothing gets forgotten and there are no excuses for tense phone calls.'
            }, {
              icon: <BookOpen data-id="element-113" />,
              title: 'Age-Specific Micro-Scripts',
              desc: 'Ready-made responses for children ages 5 through teenagers up to 18.'
            }].map((feature, i) => <div key={i} className="bg-white p-6 rounded-xl shadow-sm border border-cream-dark flex gap-4" data-id="element-114">
                  <div className="text-amber-600 shrink-0 bg-amber-50 p-3 rounded-lg h-fit" data-id="element-115">
                    {feature.icon}
                  </div>
                  <div data-id="element-116">
                    <h3 className="font-bold text-lg mb-2 text-slate-800" data-id="element-117">
                      {feature.title}
                    </h3>
                    <p className="text-charcoal/70" data-id="element-118">{feature.desc}</p>
                  </div>
                </div>)}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* WHO THIS IS FOR / NOT FOR */}
      <section className="py-20 px-4 bg-white border-y border-cream-dark" data-id="element-119">
        <div className="max-w-4xl mx-auto" data-id="element-120">
          <div className="grid md:grid-cols-2 gap-12" data-id="element-121">
            <FadeIn data-id="element-122">
              <h3 className="font-serif text-2xl font-bold mb-6 flex items-center gap-2 text-slate-800" data-id="element-123">
                <CheckCircle2 className="text-sage" data-id="element-124" /> Who this is for:
              </h3>
              <ul className="space-y-4" data-id="element-125">
                {["Recently separated parents who want to prioritize their children's peace.", 'Latino families in the US facing the added challenge of language and cultural clashes between houses.', 'Parents who feel paralyzing guilt every time they drop their kids off at the other house.', 'Men and women seeking functional co-parenting even when the relationship with their ex is difficult.'].map((item, i) => <li key={i} className="flex items-start gap-3 text-charcoal/80" data-id="element-126">
                    <Check className="text-sage shrink-0 mt-1" size={20} data-id="element-127" />
                    <span data-id="element-128">{item}</span>
                  </li>)}
              </ul>
            </FadeIn>

            <FadeIn delay={0.2} data-id="element-129">
              <h3 className="font-serif text-2xl font-bold mb-6 flex items-center gap-2 text-slate-800" data-id="element-130">
                <XCircle className="text-red-400" data-id="element-131" /> Who this is NOT for:
              </h3>
              <ul className="space-y-4" data-id="element-132">
                {["Cases of physical abuse or situations where the child's safety is at real risk.", 'People looking for tools to "beat" their ex legally (this is about emotional health).', "Parents unwilling to make minimal adjustments to their own routines for their child's well-being."].map((item, i) => <li key={i} className="flex items-start gap-3 text-charcoal/80" data-id="element-133">
                    <XCircle className="text-red-400 shrink-0 mt-1" size={20} data-id="element-134" />
                    <span data-id="element-135">{item}</span>
                  </li>)}
              </ul>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 px-4 bg-cream" data-id="element-136">
        <div className="max-w-3xl mx-auto" data-id="element-137">
          <FadeIn data-id="element-138">
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-center mb-16 text-slate-800" data-id="element-139">
              How does it work?
            </h2>
            <div className="space-y-8 relative before:absolute before:inset-0 before:ml-6 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-amber-200 before:to-transparent" data-id="element-140">
              {[{
              step: '1',
              title: 'Download & Read',
              desc: 'Download the guide and read Chapter 1 on the "Doorway Syndrome" today.'
            }, {
              step: '2',
              title: 'Apply the Scripts',
              desc: 'Use the crisis scripts in your next conversation or handoff with your kids.'
            }, {
              step: '3',
              title: 'Align the Rules',
              desc: 'Use the invisible negotiation templates to align basic rules with your ex without creating conflict.'
            }].map((item, i) => <div key={i} className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-141">
                  <div className="flex items-center justify-center w-12 h-12 rounded-full border-4 border-cream bg-amber-100 text-amber-700 font-bold text-xl shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 shadow-sm z-10" data-id="element-142">
                    {item.step}
                  </div>
                  <div className="w-[calc(100%-4rem)] md:w-[calc(50%-3rem)] bg-white p-6 rounded-xl shadow-sm border border-cream-dark" data-id="element-143">
                    <h3 className="font-bold text-lg mb-2 text-slate-800" data-id="element-144">
                      {item.title}
                    </h3>
                    <p className="text-charcoal/70" data-id="element-145">{item.desc}</p>
                  </div>
                </div>)}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* PROOF */}
      <section className="py-24 px-4 bg-slate-800 text-cream" data-id="element-146">
        <div className="max-w-3xl mx-auto text-center" data-id="element-147">
          <FadeIn data-id="element-148">
            <Heart className="w-12 h-12 text-amber-500 mx-auto mb-6" data-id="element-149" />
            <h2 className="font-serif text-3xl sm:text-4xl font-bold mb-8 text-white" data-id="element-150">
              What changes in 7 days?
            </h2>
            <div className="bg-slate-700/50 p-8 rounded-2xl border border-slate-600 text-left space-y-6 text-lg text-slate-300" data-id="element-151">
              <p data-id="element-152">
                In the first week, you'll notice your child's cortisol (stress)
                levels drop. Sunday transitions will stop being a war zone and
                you'll start getting calmer responses instead of slammed doors
                or tears.
              </p>
              <p className="font-medium text-amber-400" data-id="element-153">
                Your child gets their right to be a child back — no longer the
                messenger between adults.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-4 bg-cream" data-id="element-154">
        <div className="max-w-3xl mx-auto" data-id="element-155">
          <FadeIn data-id="element-156">
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-center mb-12 text-slate-800" data-id="element-157">
              Frequently Asked Questions
            </h2>
            <div className="space-y-6" data-id="element-158">
              {[{
              q: 'Do I have to talk a lot with my ex for this to work?',
              a: 'No. The method is designed to work even with minimal communication through text or business-style emails.'
            }, {
              q: 'Does it work with rebellious teenagers?',
              a: 'Yes, the guide includes a specific section for teens ages 12 to 18 and how to handle their resistance to the two-house hierarchy.'
            }, {
              q: 'Will my child stop crying immediately?',
              a: "The crying will decrease dramatically once you apply the exit ritual and the 15-Minute Rule, giving them the security they're currently missing."
            }, {
              q: 'What if my ex refuses to cooperate at all?',
              a: 'The guide teaches you how to fortify "your house" so it becomes the safe harbor that regulates your child\'s nervous system, regardless of what happens over there.'
            }].map((faq, i) => <div key={i} className="bg-white p-6 rounded-xl shadow-sm border border-cream-dark" data-id="element-159">
                  <h3 className="font-bold text-lg mb-3 text-slate-800 flex gap-3" data-id="element-160">
                    <span className="text-amber-600" data-id="element-161">Q.</span> {faq.q}
                  </h3>
                  <p className="text-charcoal/80 pl-7" data-id="element-162">{faq.a}</p>
                </div>)}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* OFFER & CTA */}
      <section id="pricing" className="py-24 px-4 bg-white border-t border-cream-dark" data-id="element-163">
        <div className="max-w-3xl mx-auto text-center" data-id="element-164">
          <FadeIn data-id="element-165">
            <h2 className="font-serif text-3xl sm:text-4xl font-bold mb-6 text-slate-800" data-id="element-166">
              Protect your children's hearts today for less than the price of a
              coffee.
            </h2>
            <p className="text-xl text-charcoal/70 mb-12" data-id="element-167">
              Get the complete guide "Two Houses, the Kids & Divorce" and stop
              the cycle of family anguish right now.
            </p>

            <div className="bg-cream-dark/30 p-8 sm:p-12 rounded-3xl border-2 border-amber-200 shadow-xl max-w-xl mx-auto" data-id="element-168">
              <p className="text-sm font-bold text-amber-700 uppercase tracking-widest mb-2" data-id="element-169">
                Special Launch Price
              </p>
              <div className="text-6xl sm:text-7xl font-bold text-slate-800 mb-8 font-serif" data-id="element-170">
                $7.35
              </div>
              <CTAButton className="w-full text-xl py-5" data-id="element-171" />
            </div>
          </FadeIn>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-slate-900 text-slate-400 py-10 text-center text-sm" data-id="element-172">
        <p data-id="element-173">
          © {new Date().getFullYear()} Mirror House Method. All rights
          reserved.
        </p>
        <div className="mt-4 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs" data-id="element-174">
          <button onClick={() => setActiveModal('terms')} className="hover:text-white transition-colors underline underline-offset-2" data-id="element-175">
            Terms & Conditions
          </button>
          <span className="text-slate-600" data-id="element-176">|</span>
          <button onClick={() => setActiveModal('privacy')} className="hover:text-white transition-colors underline underline-offset-2" data-id="element-177">
            Privacy Policy
          </button>
          <span className="text-slate-600" data-id="element-178">|</span>
          <button onClick={() => setActiveModal('refund')} className="hover:text-white transition-colors underline underline-offset-2" data-id="element-179">
            Refund Policy
          </button>
        </div>
        <p className="mt-4 text-xs opacity-60 max-w-2xl mx-auto px-4" data-id="element-180">
          This material is for educational purposes and does not replace
          professional legal advice or psychological therapy.
        </p>
      </footer>

      {/* LEGAL MODALS */}
      <AnimatePresence data-id="element-181">
        {activeModal && <LegalModalComponent activeModal={activeModal} onClose={() => setActiveModal(null)} data-id="element-182" />}
      </AnimatePresence>
    </div>;
}